import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, X, Zap, ArrowRight, HelpCircle, ChevronDown, ChevronUp } from "lucide-react";
import { AuthContext } from "@/App";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_ORDER = ["free", "starter", "professional", "enterprise"];
const PLAN_COLORS = {
  free: "border-gray-500/30",
  starter: "border-blue-500/30",
  professional: "border-[var(--qn-primary)] ring-2 ring-[var(--qn-primary)]/20",
  enterprise: "border-amber-500/30",
};
const PLAN_BADGES = {
  free: "", starter: "", professional: "Most Popular", enterprise: "",
};

const FAQ = [
  { q: "Can I change plans anytime?", a: "Yes. Upgrade or downgrade at any time. When upgrading, you get immediate access to the new plan's features. When downgrading, the change takes effect at the end of your current billing period." },
  { q: "What happens when I hit my API call limit?", a: "You'll receive email alerts at 80% and 95% usage. Once you hit 100%, additional API calls will return a 429 error with an upgrade prompt. Your existing data and settings remain intact." },
  { q: "Do you offer refunds?", a: "We offer a 14-day money-back guarantee on all paid plans. Contact support within 14 days of your first payment for a full refund, no questions asked." },
  { q: "Is there a free trial?", a: "The Free tier is permanently free with 1,000 API calls/month. No credit card required. You can use it indefinitely to evaluate the platform before upgrading." },
  { q: "What payment methods do you accept?", a: "We accept all major credit and debit cards (Visa, Mastercard, Amex) via Stripe. Enterprise customers can pay via invoice/wire transfer." },
  { q: "Do you offer volume discounts?", a: "Enterprise plans include custom pricing for high-volume usage. Contact sales for volume pricing above 1M API calls/month." },
];

export default function PricingPage() {
  const [interval, setInterval] = useState(() => {
    try { return sessionStorage.getItem("qn_pricing_interval") || "month"; } catch { return "month"; }
  });
  const [plans, setPlans] = useState(null);
  const [features, setFeatures] = useState([]);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const authCtx = useContext(AuthContext);
  const user = authCtx?.user;
  const navigate = useNavigate();

  // Persist interval selection in session
  useEffect(() => {
    try { sessionStorage.setItem("qn_pricing_interval", interval); } catch {}
  }, [interval]);

  useEffect(() => {
    axios.get(`${API}/billing/plans`).then(r => {
      setPlans(r.data.plans);
      setFeatures(r.data.features);
    }).catch(() => {});
  }, []);

  const handleSelectPlan = async (planId) => {
    if (planId === "free") {
      navigate("/login");
      return;
    }
    if (!user) {
      navigate("/login?returnTo=/pricing");
      return;
    }
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/billing/create-checkout-session`,
        { plan: planId, interval },
        { headers: { "x-origin-url": origin } }
      );
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to start checkout");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A1A] text-white" data-testid="pricing-page">
      {/* Header */}
      <div className="max-w-6xl mx-auto px-6 pt-16 pb-8 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Badge className="bg-[var(--qn-primary)]/15 text-[var(--qn-primary)] mb-4 text-xs">Pricing</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Simple, Transparent Pricing</h1>
          <p className="text-lg text-white/60 max-w-2xl mx-auto mb-8">
            Start free. Upgrade when you need more. No hidden fees. Cancel anytime.
          </p>

          {/* Interval Toggle */}
          <div className="inline-flex items-center gap-3 bg-white/5 rounded-full p-1 border border-white/10" data-testid="interval-toggle">
            <button onClick={() => setInterval("month")} className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${interval === "month" ? "bg-[var(--qn-primary)] text-white" : "text-white/50 hover:text-white/70"}`} data-testid="interval-month">
              Monthly
            </button>
            <button onClick={() => setInterval("year")} className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${interval === "year" ? "bg-[var(--qn-primary)] text-white" : "text-white/50 hover:text-white/70"}`} data-testid="interval-year">
              Annual <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">Save 17%</Badge>
            </button>
          </div>
        </motion.div>
      </div>

      {/* Plans Grid */}
      <div className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-4 gap-5">
          {plans && PLAN_ORDER.map((planId, i) => {
            const p = plans[planId];
            if (!p) return null;
            const price = interval === "year" ? p.annual_price : p.monthly_price;
            const isPopular = PLAN_BADGES[planId];
            const isCurrent = user?.plan === planId;

            return (
              <motion.div key={planId} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                <Card className={`bg-[#111122] border ${PLAN_COLORS[planId]} h-full relative overflow-hidden`}>
                  {isPopular && (
                    <div className="absolute top-0 right-0 bg-[var(--qn-primary)] text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg">{isPopular}</div>
                  )}
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-medium text-white/80">{p.name}</CardTitle>
                    <div className="flex items-baseline gap-1 mt-2">
                      <span className="text-4xl font-bold">${interval === "year" ? Math.round(price / 12) : price}</span>
                      {price > 0 && <span className="text-sm text-white/40">/mo</span>}
                    </div>
                    {interval === "year" && price > 0 && (
                      <div className="text-xs text-white/40">${price}/year billed annually</div>
                    )}
                    {price === 0 && <div className="text-xs text-white/40">Free forever</div>}
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-sm text-white/60">
                      <div className="flex items-center gap-2 py-1.5"><Check className="w-4 h-4 text-[var(--qn-primary)] shrink-0" /> {p.api_calls_limit === -1 ? "Unlimited" : `${p.api_calls_limit.toLocaleString()}`} API calls/mo</div>
                      <div className="flex items-center gap-2 py-1.5"><Check className="w-4 h-4 text-[var(--qn-primary)] shrink-0" /> {p.max_providers === -1 ? "Unlimited" : p.max_providers} providers</div>
                      <div className="flex items-center gap-2 py-1.5"><Check className="w-4 h-4 text-[var(--qn-primary)] shrink-0" /> {p.max_team_members} team member{p.max_team_members > 1 ? "s" : ""}</div>
                      <div className="flex items-center gap-2 py-1.5"><Check className="w-4 h-4 text-[var(--qn-primary)] shrink-0" /> {p.max_vault_keys === -1 ? "Unlimited" : p.max_vault_keys} vault key{p.max_vault_keys !== 1 ? "s" : ""}</div>
                      <div className="flex items-center gap-2 py-1.5"><Check className="w-4 h-4 text-[var(--qn-primary)] shrink-0" /> {p.audit_log_days}-day audit logs</div>
                      {p.max_self_hosted > 0 && (
                        <div className="flex items-center gap-2 py-1.5"><Check className="w-4 h-4 text-[var(--qn-primary)] shrink-0" /> {p.max_self_hosted === -1 ? "Unlimited" : p.max_self_hosted} self-hosted</div>
                      )}
                    </div>
                    <Button
                      className={`w-full ${planId === "professional" ? "bg-[var(--qn-primary)] hover:bg-[var(--qn-primary)]/90" : "bg-white/10 hover:bg-white/15"}`}
                      onClick={() => handleSelectPlan(planId)}
                      disabled={isCurrent}
                      data-testid={`select-plan-${planId}`}
                    >
                      {isCurrent ? "Current Plan" : planId === "free" ? "Start Free" : planId === "enterprise" ? "Contact Sales" : "Get Started"}
                      {!isCurrent && <ArrowRight className="w-4 h-4 ml-2" />}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Feature Comparison Table */}
      {features.length > 0 && (
        <div className="max-w-6xl mx-auto px-6 pb-16">
          <h2 className="text-2xl font-bold text-center mb-8">Full Feature Comparison</h2>
          <div className="bg-[#111122] rounded-xl border border-white/10 overflow-hidden" data-testid="feature-comparison">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-white/50 font-medium">Feature</th>
                  {PLAN_ORDER.map(p => (
                    <th key={p} className="p-4 text-center font-medium capitalize">{p}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features.map((row, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-4 text-white/60">{row.feature}</td>
                    {PLAN_ORDER.map(p => (
                      <td key={p} className="p-4 text-center">
                        {row[p] === "---" ? <X className="w-4 h-4 text-white/20 mx-auto" /> :
                         row[p] === "Yes" ? <Check className="w-4 h-4 text-[var(--qn-primary)] mx-auto" /> :
                         <span className="text-white/70">{row[p]}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* FAQ */}
      <div className="max-w-3xl mx-auto px-6 pb-24">
        <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
        <div className="space-y-3" data-testid="pricing-faq">
          {FAQ.map((faq, i) => (
            <div key={i} className="bg-[#111122] rounded-lg border border-white/10 overflow-hidden">
              <button className="w-full flex items-center justify-between p-4 text-left" onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} data-testid={`faq-${i}`}>
                <span className="font-medium text-sm">{faq.q}</span>
                {expandedFaq === i ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
              </button>
              {expandedFaq === i && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="px-4 pb-4">
                  <p className="text-sm text-white/60">{faq.a}</p>
                </motion.div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
